import { useState } from 'react';
import { Plus } from 'lucide-react';
import { formatEur } from './charts/IbcsTooltip';
import type { IntercompanyLoan } from '../../types/cashCockpit';
import { GROUP_COMPANIES, SUPPORTED_CURRENCIES } from '../../types/cashCockpit';
import { fetchApi } from '../../services/api';

interface Props {
    data: IntercompanyLoan[];
    canEdit: boolean;
    onReload: () => void;
}

const SOURCE_BADGE: Record<IntercompanyLoan['source'], string> = {
    BankAccount: 'bg-blue-50 text-blue-700 border-blue-200',
    GL: 'bg-purple-50 text-purple-700 border-purple-200',
    Manual: 'bg-amber-50 text-amber-700 border-amber-200',
};

export default function IntercompanyTab({ data, canEdit, onReload }: Props) {
    const [showForm, setShowForm] = useState(false);

    const receivables = data.filter(l => l.direction === 'Receivable');
    const payables = data.filter(l => l.direction === 'Payable');
    const unknown = data.filter(l => l.direction === 'Unknown');

    const totalReceivable = receivables.reduce((s, l) => s + l.amountEUR, 0);
    const totalPayable = payables.reduce((s, l) => s + Math.abs(l.amountEUR), 0);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                    Sources: bank accounts classified as IntercompanyLoan + G/L account mappings (Settings → IC Mapping) + manual entries below.
                </div>
                {canEdit && (
                    <button
                        onClick={() => setShowForm(true)}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        <Plus className="w-3 h-3" /> Add Intercompany Loan
                    </button>
                )}
            </div>

            <div className="grid grid-cols-3 gap-4">
                <Tile label="Group Receivable (IC)" value={totalReceivable} semantic="positive" />
                <Tile label="Group Payable (IC)" value={totalPayable} semantic="negative" />
                <Tile label="Net IC Position" value={totalReceivable - totalPayable} />
            </div>

            {data.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-500 text-sm">
                    No intercompany loans. Configure IC G/L mappings in Settings, classify accounts as IntercompanyLoan in Bank Balance, or add a manual entry above.
                </div>
            ) : (
                <>
                    <LoanTable title="Receivables (money owed TO the entity)" loans={receivables} />
                    <LoanTable title="Payables (money owed BY the entity)" loans={payables} />
                    {unknown.length > 0 && <LoanTable title="Unclassified direction" loans={unknown} />}
                </>
            )}

            {showForm && canEdit && (
                <AddIntercompanyModal
                    onClose={() => setShowForm(false)}
                    onSaved={() => { setShowForm(false); onReload(); }}
                />
            )}
        </div>
    );
}

function LoanTable({ title, loans }: { title: string; loans: IntercompanyLoan[] }) {
    if (loans.length === 0) return null;
    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <h3 className="text-sm font-semibold text-gray-900 px-4 py-3 border-b border-gray-200">{title}</h3>
            <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-600">
                    <tr>
                        <th className="px-3 py-2 text-left">Source</th>
                        <th className="px-3 py-2 text-left">Entity</th>
                        <th className="px-3 py-2 text-left">Loan Account</th>
                        <th className="px-3 py-2 text-left">Counterparty</th>
                        <th className="px-3 py-2 text-right">Amount FCY</th>
                        <th className="px-3 py-2 text-right">Amount EUR</th>
                        <th className="px-3 py-2 text-right">Interest</th>
                        <th className="px-3 py-2 text-right">Maturity</th>
                    </tr>
                </thead>
                <tbody>
                    {loans.map(l => (
                        <tr key={l.bankID} className="border-t border-gray-100">
                            <td className="px-3 py-1.5">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${SOURCE_BADGE[l.source]}`}>
                                    {l.source}
                                </span>
                            </td>
                            <td className="px-3 py-1.5 font-semibold uppercase">{l.companyCode}</td>
                            <td className="px-3 py-1.5">{l.bankAccountName}</td>
                            <td className="px-3 py-1.5">{l.counterparty ?? '—'}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                                {l.amountFCY.toLocaleString('de-CH')} {l.currencyCode}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatEur(l.amountEUR)}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                                {l.interestRate != null ? `${(l.interestRate * 100).toFixed(2)}%` : '—'}
                            </td>
                            <td className="px-3 py-1.5 text-right">{l.maturityDate ?? '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Tile({ label, value, semantic }: { label: string; value: number; semantic?: 'positive' | 'negative' }) {
    const color = semantic === 'positive' ? 'text-[#2C7BB6]' : semantic === 'negative' ? 'text-[#D7191C]' : 'text-gray-900';
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
            <div className={`text-xl font-semibold font-mono tabular-nums ${color}`}>{formatEur(value)}</div>
        </div>
    );
}

interface AddModalProps {
    onClose: () => void;
    onSaved: () => void;
}

function AddIntercompanyModal({ onClose, onSaved }: AddModalProps) {
    const [companyCode, setCompanyCode] = useState<string>('bin');
    const [counterpartyCompanyCode, setCounterpartyCompanyCode] = useState<string>('dbi');
    const [loanName, setLoanName] = useState('');
    const [currencyCode, setCurrencyCode] = useState<string>('EUR');
    const [principalAmount, setPrincipalAmount] = useState('');
    const [interestRate, setInterestRate] = useState('');
    const [maturityDate, setMaturityDate] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const principalNum = parseFloat(principalAmount);
    const interestNum = interestRate ? parseFloat(interestRate) / 100 : null;
    const canSave =
        companyCode && counterpartyCompanyCode && loanName.trim() &&
        !Number.isNaN(principalNum) && principalNum !== 0 && !saving;

    const handleSave = async () => {
        if (!canSave) return;
        setSaving(true);
        setError(null);
        try {
            // Slug BankID from loan name + timestamp to keep uniqueness
            const slug = loanName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30);
            const bankId = `${companyCode}-icl-${slug}-${Date.now().toString(36)}`;

            // 1. Create BankAccountConfig row with AccountType='IntercompanyLoan'
            await fetchApi<{ id: number }>('/bank-balance/config', {
                method: 'POST',
                body: JSON.stringify({
                    companyCode,
                    bankId,
                    bankAccountName: loanName.trim(),
                    bankCompanyName: `IC Loan — ${counterpartyCompanyCode.toUpperCase()}`,
                    accountType: 'IntercompanyLoan',
                    dataSource: 'Manual',
                    isCashRelevant: false,
                    isActive: true,
                    interestRate: interestNum,
                    maturityDate: maturityDate || null,
                    notes: `Intercompany loan to/from ${counterpartyCompanyCode.toUpperCase()}`,
                }),
            });

            // 2. Seed initial BankBalanceEntry with the principal so the IC tab shows a value
            await fetchApi('/bank-balance', {
                method: 'POST',
                body: JSON.stringify({
                    companyCode,
                    bankId,
                    postingDate: new Date().toISOString().slice(0, 10),
                    currencyCode,
                    amountFCY: principalNum,
                    notes: 'Initial principal on IC loan creation',
                }),
            });

            onSaved();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
                <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-base font-semibold">Add Intercompany Loan</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-lg">×</button>
                </div>
                <div className="p-5 space-y-3 text-sm">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-xs">{error}</div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-xs text-gray-600">Home entity *</span>
                            <select
                                value={companyCode}
                                onChange={e => setCompanyCode(e.target.value)}
                                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 uppercase"
                            >
                                {GROUP_COMPANIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs text-gray-600">Counterparty entity *</span>
                            <select
                                value={counterpartyCompanyCode}
                                onChange={e => setCounterpartyCompanyCode(e.target.value)}
                                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 uppercase"
                            >
                                {GROUP_COMPANIES.filter(c => c !== companyCode).map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-xs text-gray-600">Loan name * (use &ldquo;Darlehen an/von X&rdquo; for direction detection)</span>
                        <input
                            type="text"
                            value={loanName}
                            onChange={e => setLoanName(e.target.value)}
                            placeholder="e.g. Darlehen an Dolder-Bigler"
                            className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5"
                        />
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                        <label className="block">
                            <span className="text-xs text-gray-600">Currency *</span>
                            <select
                                value={currencyCode}
                                onChange={e => setCurrencyCode(e.target.value)}
                                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5"
                            >
                                {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="block col-span-2">
                            <span className="text-xs text-gray-600">Principal (signed: + Receivable / − Payable) *</span>
                            <input
                                type="number" step="0.01"
                                value={principalAmount}
                                onChange={e => setPrincipalAmount(e.target.value)}
                                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 font-mono"
                            />
                        </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-xs text-gray-600">Interest rate (% p.a.)</span>
                            <input
                                type="number" step="0.01"
                                value={interestRate}
                                onChange={e => setInterestRate(e.target.value)}
                                placeholder="e.g. 2.5"
                                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 font-mono"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs text-gray-600">Maturity date</span>
                            <input
                                type="date"
                                value={maturityDate}
                                onChange={e => setMaturityDate(e.target.value)}
                                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5"
                            />
                        </label>
                    </div>
                </div>
                <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
                    >Cancel</button>
                    <button
                        onClick={handleSave}
                        disabled={!canSave}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >{saving ? 'Saving…' : 'Save'}</button>
                </div>
            </div>
        </div>
    );
}
